import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Clock, Check, X, Calendar, Phone, Mail, MessageSquare } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const TestDrivesManagement = () => {
  const [testDrives, setTestDrives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDrive, setSelectedDrive] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchTestDrives();
  }, []);

  const fetchTestDrives = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/test-drives`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestDrives(response.data);
    } catch (error) {
      console.error('Error fetching test drives:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status, notes = '') => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API}/admin/test-drives/${id}?status=${status}&notes=${encodeURIComponent(notes)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      fetchTestDrives();
      setSelectedDrive(null);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'approved': return 'bg-green-500/20 text-green-400';
      case 'cancelled': return 'bg-red-500/20 text-red-400';
      case 'completed': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const filteredDrives = filter === 'all' 
    ? testDrives 
    : testDrives.filter(d => d.status === filter);

  const pendingCount = testDrives.filter(d => d.status === 'pending').length;

  return (
    <div data-testid="test-drives-management">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Test Drive Requests</h1>
          {pendingCount > 0 && (
            <p className="text-[#ff6b35] text-sm mt-1">{pendingCount} pending request(s)</p>
          )}
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="bg-[#1a2d47] border-[#1a4d8c] text-white w-[150px]">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a2d47] border-[#1a4d8c]">
            <SelectItem value="all" className="text-white">All Requests</SelectItem>
            <SelectItem value="pending" className="text-white">Pending</SelectItem>
            <SelectItem value="approved" className="text-white">Approved</SelectItem>
            <SelectItem value="completed" className="text-white">Completed</SelectItem>
            <SelectItem value="cancelled" className="text-white">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Detail Modal */}
      {selectedDrive && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#132238] rounded-xl p-6 w-full max-w-lg">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold text-white">Test Drive Details</h2>
              <button onClick={() => setSelectedDrive(null)} className="text-[#a8b8d0] hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-[#1a2d47]/50 rounded-lg p-4">
                <p className="text-[#ff6b35] font-medium mb-2">{selectedDrive.vehicle_name}</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-[#a8b8d0]">
                    <Calendar size={14} />
                    {selectedDrive.preferred_date}
                  </div>
                  <div className="flex items-center gap-2 text-[#a8b8d0]">
                    <Clock size={14} />
                    {selectedDrive.preferred_time}
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <p className="text-white font-medium">{selectedDrive.full_name}</p>
                <p className="flex items-center gap-2 text-[#a8b8d0] text-sm">
                  <Mail size={14} /> {selectedDrive.email}
                </p>
                <p className="flex items-center gap-2 text-[#a8b8d0] text-sm">
                  <Phone size={14} /> {selectedDrive.phone}
                </p>
                {selectedDrive.message && (
                  <div className="flex items-start gap-2 text-[#a8b8d0] text-sm mt-2">
                    <MessageSquare size={14} className="mt-0.5" />
                    <span>{selectedDrive.message}</span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-4 pt-4 border-t border-[#1a2d47]">
                <span className="text-[#a8b8d0] text-sm">Update Status:</span>
                <div className="flex gap-2">
                  <button onClick={() => updateStatus(selectedDrive.id, 'approved')}
                    className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-sm hover:bg-green-500/30">
                    Approve
                  </button>
                  <button onClick={() => updateStatus(selectedDrive.id, 'completed')}
                    className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30">
                    Complete
                  </button>
                  <button onClick={() => updateStatus(selectedDrive.id, 'cancelled')}
                    className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-[#132238] rounded-xl border border-[#1a2d47] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1a2d47]">
              <tr>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">Customer</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">Vehicle</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">Date & Time</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">Status</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDrives.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-[#a8b8d0]">
                    No test drive requests found
                  </td>
                </tr>
              ) : (
                filteredDrives.map((drive) => (
                  <tr key={drive.id} className="border-t border-[#1a2d47] hover:bg-[#1a2d47]/50 cursor-pointer"
                    onClick={() => setSelectedDrive(drive)}>
                    <td className="px-4 py-3">
                      <p className="text-white font-medium">{drive.full_name}</p>
                      <p className="text-[#a8b8d0] text-sm">{drive.email}</p>
                    </td>
                    <td className="px-4 py-3 text-[#a8b8d0]">{drive.vehicle_name || 'Not specified'}</td>
                    <td className="px-4 py-3">
                      <p className="text-white text-sm">{drive.preferred_date}</p>
                      <p className="text-[#a8b8d0] text-sm">{drive.preferred_time}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs capitalize ${getStatusColor(drive.status)}`}>
                        {drive.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button className="text-[#ff6b35] hover:text-[#ffc107] text-sm font-medium">
                        View Details →
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TestDrivesManagement;
